import {useEffect, useState} from "react";
import {faSpinner} from "@fortawesome/free-solid-svg-icons";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {DatePicker} from "zaman";
import moment from "jalali-moment";
import Layout from "../../components/Layout";
import ApiRoute, {shopId} from "../../helper/api";

const currentDate = new Date();
const today = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(currentDate.getDate()).padStart(2, '0')}`;

function extractPeriods(response) {
    const items = response?.results?.data ?? response?.results ?? [];

    if (!Array.isArray(items)) return [];

    return items.map((period) => ({
        id: period._id ?? period.id ?? period.period_id,
        title: period.title ?? period.name ?? period.period_title ?? 'بدون عنوان'
    })).filter((period) => period.id);
}

function formatCapacity(slot) {
    if (slot.status === 'unlimited') return 'نامحدود';
    return slot.capacity ?? '—';
}

function toGregorianDate(date) {
    return moment(date).format('YYYY-MM-DD');
}

function CapacityStatus({status}) {
    const isFull = status === 'full';
    const isUnlimited = status === 'unlimited';
    const text = isFull ? 'تکمیل' : isUnlimited ? 'نامحدود' : 'دارای ظرفیت';

    return (
        <span className={`inline-flex rounded-full px-2.5 py-1 text-xs ${
            isFull
                ? 'bg-red-100 text-red-700'
                : isUnlimited
                    ? 'bg-blue-100 text-blue-700'
                    : 'bg-green-100 text-green-700'
        }`}>
            {text}
        </span>
    );
}

function SendDeliveryTimeReport() {
    const [filters, setFilters] = useState({
        dateFrom: today,
        dateTo: '',
        deliveryType: 'default',
        periodId: ''
    });
    const [periods, setPeriods] = useState([]);
    const [report, setReport] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isLoadingPeriods, setIsLoadingPeriods] = useState(false);
    const [error, setError] = useState('');
    const [hasSearched, setHasSearched] = useState(false);

    useEffect(() => {
        let isCurrent = true;

        async function loadPeriods() {
            setIsLoadingPeriods(true);
            setPeriods([]);
            setFilters((current) => ({...current, periodId: ''}));

            const api = filters.deliveryType === 'in_person'
                ? ApiRoute.shipment.deliveryInPerson.index
                : ApiRoute.shipment.delivery.index;
            try {
                const response = await api.fn({shop_id: shopId(), page: 1, per_page: 1000});

                if (isCurrent) {
                    setPeriods(extractPeriods(response));
                }
            } catch {
                if (isCurrent) {
                    setPeriods([]);
                }
            } finally {
                if (isCurrent) {
                    setIsLoadingPeriods(false);
                }
            }
        }

        loadPeriods();
        return () => {
            isCurrent = false;
        };
    }, [filters.deliveryType]);

    function updateFilter(name, value) {
        setFilters((current) => ({...current, [name]: value}));
    }

    async function loadReport(event) {
        event?.preventDefault();
        setError('');

        if (!filters.dateFrom) {
            setError('تاریخ شروع الزامی است.');
            return;
        }

        if (filters.dateTo) {
            const from = new Date(`${filters.dateFrom}T00:00:00`);
            const to = new Date(`${filters.dateTo}T00:00:00`);
            const days = Math.round((to - from) / 86400000);

            if (days < 0) {
                setError('تاریخ پایان باید بعد از تاریخ شروع باشد.');
                return;
            }
            if (days > 30) {
                setError('بازهٔ گزارش حداکثر می‌تواند ۳۱ روز باشد.');
                return;
            }
        }

        const payload = {
            date_from: filters.dateFrom,
            delivery_type: filters.deliveryType,
            period_id: filters.periodId || null,
            include_inactive: false
        };
        if (filters.dateTo) payload.date_to = filters.dateTo;

        setIsLoading(true);
        setHasSearched(true);
        setReport([]);

        try {
            const response = await ApiRoute.shipment.delivery.capacityReport.fn(payload);
            const days = Array.isArray(response) ? response : response?.results;

            if (Array.isArray(days)) {
                setReport(days);
            } else {
                setError('دریافت گزارش با خطا روبه‌رو شد. لطفاً دوباره تلاش کنید.');
            }
        } catch {
            setError('دریافت گزارش با خطا روبه‌رو شد. لطفاً دوباره تلاش کنید.');
        } finally {
            setIsLoading(false);
        }
    }

    return (
        <Layout>
            <div>
                <div className="page-title mb-3">
                    <span className="inline-block bg-white pl-3">گزارش ظرفیت ارسال</span>
                </div>

                <form className="main-content mb-4 p-4" onSubmit={loadReport}>
                    <div className="grid grid-cols-1 gap-4 text-xs md:grid-cols-2 xl:grid-cols-5">
                        <label className="grid gap-2">
                            <span>تاریخ شروع</span>
                            <DatePicker
                                key={filters.dateFrom}
                                defaultValue={filters.dateFrom}
                                inputClass="bordered-input w-full"
                                inputAttributes={{'aria-label': 'تاریخ شروع'}}
                                onChange={({value}) => updateFilter('dateFrom', toGregorianDate(value))}
                            />
                        </label>
                        <label className="grid gap-2">
                            <span>تاریخ پایان (اختیاری)</span>
                            <DatePicker
                                key={filters.dateTo || 'date-to-empty'}
                                defaultValue={filters.dateTo || undefined}
                                inputClass="bordered-input w-full"
                                inputAttributes={{'aria-label': 'تاریخ پایان'}}
                                onChange={({value}) => updateFilter('dateTo', toGregorianDate(value))}
                            />
                        </label>
                        <label className="grid gap-2">
                            <span>نوع ارسال</span>
                            <select
                                className="bordered-input"
                                value={filters.deliveryType}
                                onChange={(event) => updateFilter('deliveryType', event.target.value)}
                            >
                                <option value="default">ارسال عادی</option>
                                <option value="in_person">تحویل حضوری</option>
                            </select>
                        </label>
                        <label className="grid gap-2">
                            <span>برنامهٔ ارسال (اختیاری)</span>
                            <select
                                className="bordered-input"
                                value={filters.periodId}
                                onChange={(event) => updateFilter('periodId', event.target.value)}
                                disabled={isLoadingPeriods}
                            >
                                <option value="">همهٔ برنامه‌ها</option>
                                {periods.map((period) => (
                                    <option key={period.id} value={period.id}>{period.title}</option>
                                ))}
                            </select>
                        </label>
                        <div className="flex items-end">
                            <button className="btn-purple w-full py-2" disabled={isLoading} type="submit">
                                {isLoading ? 'در حال دریافت...' : 'نمایش گزارش'}
                            </button>
                        </div>
                    </div>
                    <p className="mt-3 text-xs text-gray-500">بازهٔ گزارش حداکثر ۳۱ روز است.</p>
                    {error && <p className="mt-2 text-xs text-red-600" role="alert">{error}</p>}
                </form>

                <div className="main-content overflow-x-auto p-4">
                    {isLoading && (
                        <div className="flex items-center justify-center gap-3 py-12 text-sm text-gray-500">
                            <FontAwesomeIcon icon={faSpinner} spin />
                            <span>در حال دریافت گزارش ظرفیت ارسال...</span>
                        </div>
                    )}

                    {!isLoading && hasSearched && !error && report.length === 0 && (
                        <div className="py-12 text-center text-sm text-gray-500">برای این فیلتر، داده‌ای یافت نشد.</div>
                    )}

                    {!isLoading && !hasSearched && (
                        <div className="py-12 text-center text-sm text-gray-500">فیلترها را انتخاب کرده و گزارش را نمایش دهید.</div>
                    )}

                    {!isLoading && !error && report.map((day) => (
                        <section className="mb-6 last:mb-0" key={day.date}>
                            <h2 className="mb-2 rounded bg-gray-100 px-3 py-2 text-sm font-semibold text-gray-700">
                                {day.date_fa || day.date}
                            </h2>
                            <table className="w-full min-w-[900px] table-fixed border-separate border-spacing-y-2 text-xs">
                                <colgroup>
                                    <col className="w-[12%]" />
                                    <col className="w-[22%]" />
                                    <col className="w-[14%]" />
                                    <col className="w-[12%]" />
                                    <col className="w-[12%]" />
                                    <col className="w-[14%]" />
                                    <col className="w-[14%]" />
                                </colgroup>
                                <thead className="text-gray-500">
                                <tr>
                                    <th className="border-b border-gray-200 p-3 text-right font-medium">تاریخ</th>
                                    <th className="border-b border-gray-200 p-3 text-right font-medium">عنوان برنامه</th>
                                    <th className="border-b border-gray-200 p-3 text-center font-medium">ساعت بازه</th>
                                    <th className="border-b border-gray-200 p-3 text-center font-medium">ظرفیت کل</th>
                                    <th className="border-b border-gray-200 p-3 text-center font-medium">استفاده‌شده</th>
                                    <th className="border-b border-gray-200 p-3 text-center font-medium">باقی‌مانده</th>
                                    <th className="border-b border-gray-200 p-3 text-center font-medium">وضعیت</th>
                                </tr>
                                </thead>
                                <tbody>
                                {(day.slots ?? []).length === 0 ? (
                                    <tr>
                                        <td className="p-4 text-center text-xs text-gray-500" colSpan={7}>
                                            بازه‌ای برای این تاریخ ثبت نشده است.
                                        </td>
                                    </tr>
                                ) : (day.slots ?? []).map((slot, index) => {
                                    const hasUsedCapacity = Number(slot.used_capacity) > 0;
                                    const rowStyle = slot.status === 'full'
                                        ? {backgroundColor: '#FEF2F2'}
                                        : hasUsedCapacity
                                            ? {backgroundColor: '#FFFBEB'}
                                            : undefined;

                                    return (
                                    <tr className="border-b border-gray-100" style={rowStyle} key={`${day.date}-${slot.pick_id ?? index}`}>
                                        <td className="rounded-r-lg p-3 text-right align-middle whitespace-nowrap">{day.date_fa || day.date}</td>
                                        <td className="p-3 text-right align-middle"><span className="block truncate" title={slot.period_title || '—'}>{slot.period_title || '—'}</span></td>
                                        <td className="p-3 text-center align-middle whitespace-nowrap">{slot.start} تا {slot.end}</td>
                                        <td className="p-3 text-center align-middle whitespace-nowrap">{formatCapacity(slot)}</td>
                                        <td className="p-3 text-center align-middle whitespace-nowrap">{slot.status === 'unlimited' ? 'نامحدود' : slot.used_capacity ?? '—'}</td>
                                        <td className="p-3 text-center align-middle whitespace-nowrap">{slot.status === 'unlimited' ? 'نامحدود' : slot.remaining_capacity ?? '—'}</td>
                                        <td className="rounded-l-lg p-3 text-center align-middle"><CapacityStatus status={slot.status}/></td>
                                    </tr>
                                    );
                                })}
                                </tbody>
                            </table>
                        </section>
                    ))}
                </div>
            </div>
        </Layout>
    );
}

export default SendDeliveryTimeReport;
